import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Save, Mail, Cloud } from "lucide-react";

export default function Settings() {
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("general");

  async function load() {
    setLoading(true);
    try { const { data } = await api.get("/settings/"); setForm(data); }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function save() {
    setSaving(true);
    try {
      await api.patch("/settings/", form);
      alert("Configuración guardada.");
    } catch (e) { alert("Error: " + (e.response?.data?.detail || e.message)); }
    finally { setSaving(false); }
  }

  if (loading) return <div className="card text-dim">Cargando...</div>;

  return (
    <div data-testid="settings-module">
      <h1 className="card-h1">Configuración del Tenant</h1>
      <div className="tabs-bar">
        <button className={`tab ${tab === "general" ? "active" : ""}`} onClick={() => setTab("general")} data-testid="settings-tab-general">General</button>
        <button className={`tab ${tab === "drive" ? "active" : ""}`} onClick={() => setTab("drive")} data-testid="settings-tab-drive"><Cloud size={12} /> Google Drive</button>
        <button className={`tab ${tab === "smtp" ? "active" : ""}`} onClick={() => setTab("smtp")} data-testid="settings-tab-smtp"><Mail size={12} /> SMTP</button>
        <button className={`tab ${tab === "alerts" ? "active" : ""}`} onClick={() => setTab("alerts")} data-testid="settings-tab-alerts">Alertas y umbrales</button>
      </div>

      {tab === "general" && (
        <div className="card mb-4">
          <h3 className="text-lg font-semibold mb-3">Identidad del tenant</h3>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Nombre de la empresa" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="settings-name" />
            <Field label="Nombre de la instalación" value={form.installation_name} onChange={(v) => setForm({ ...form, installation_name: v })} testId="settings-installation" />
            <Field label="URL del logo" value={form.company_logo_url} onChange={(v) => setForm({ ...form, company_logo_url: v })} />
            <Field label="Color primario" value={form.primary_color} onChange={(v) => setForm({ ...form, primary_color: v })} />
            <Field label="Color acento" value={form.accent_color} onChange={(v) => setForm({ ...form, accent_color: v })} />
            <Field label="Zona horaria" value={form.timezone} onChange={(v) => setForm({ ...form, timezone: v })} />
          </div>
        </div>
      )}

      {tab === "drive" && (
        <div className="card mb-4">
          <h3 className="text-lg font-semibold mb-3">Google Drive</h3>
          <div className="card mb-3" style={{ background: "rgba(59,130,246,0.06)", border: "1px solid var(--info)" }}>
            <div className="text-sm">
              <b>Estado: {form.google_client_id && form.google_client_secret ? "✅ Credenciales configuradas — pendiente conectar" : "⚪ No configurado"}</b>
            </div>
            <div className="text-xs text-dim mt-2">
              Define las credenciales OAuth y el Folder ID destino. Cuando ambos estén presentes, ve a <b>Administración → Google Drive</b> para iniciar el flujo OAuth de autorización.
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Google Client ID" value={form.google_client_id} onChange={(v) => setForm({ ...form, google_client_id: v })} testId="settings-google-id" helper="OAuth 2.0 Web Application Client ID de Google Cloud Console" />
            <Field label="Google Client Secret" type="password" value={form.google_client_secret} onChange={(v) => setForm({ ...form, google_client_secret: v })} testId="settings-google-secret" />
            <Field label="Drive Folder ID (raíz)" value={form.drive_root_folder_id} onChange={(v) => setForm({ ...form, drive_root_folder_id: v })} testId="settings-drive-folder" helper="ID de la carpeta raíz donde se subirán los archivos" />
          </div>
          <div className="mt-3 text-xs text-dim">
            🛈 Mientras no esté conectado, todos los archivos se almacenan localmente en el servidor sin pérdida de funcionalidad.
          </div>
        </div>
      )}

      {tab === "smtp" && (
        <div className="card mb-4">
          <h3 className="text-lg font-semibold mb-3">SMTP para notificaciones por correo</h3>
          <div className="card mb-3" style={{ background: "rgba(243,156,18,0.06)", border: "1px solid var(--warn)" }}>
            <div className="text-sm">
              <b>Estado: {form.smtp_host && form.smtp_user && form.smtp_pass ? "✅ SMTP configurado" : "⚪ No configurado — solo notificaciones in-app"}</b>
            </div>
            <div className="text-xs text-dim mt-2">
              Configura tu servidor SMTP para enviar alertas automáticas a Admin/Coordinador cuando un permiso, informe, estudio o RAC esté por vencer (30 días y 7 días antes).
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="SMTP Host" value={form.smtp_host} onChange={(v) => setForm({ ...form, smtp_host: v })} testId="smtp-host" helper="smtp.gmail.com / smtp.sendgrid.net / ..." />
            <Field label="SMTP Port" type="number" value={form.smtp_port} onChange={(v) => setForm({ ...form, smtp_port: Number(v) })} testId="smtp-port" helper="587 (TLS) / 465 (SSL)" />
            <Field label="SMTP Usuario" value={form.smtp_user} onChange={(v) => setForm({ ...form, smtp_user: v })} testId="smtp-user" />
            <Field label="SMTP Contraseña" type="password" value={form.smtp_pass} onChange={(v) => setForm({ ...form, smtp_pass: v })} testId="smtp-pass" />
            <Field label="Remitente (From)" value={form.smtp_from} onChange={(v) => setForm({ ...form, smtp_from: v })} testId="smtp-from" helper="alertas@miempresa.com" />
          </div>
        </div>
      )}

      {tab === "alerts" && (
        <div className="card mb-4">
          <h3 className="text-lg font-semibold mb-3">Alertas y umbrales</h3>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Días alerta crítica" type="number" value={form.alert_days_critical} onChange={(v) => setForm({ ...form, alert_days_critical: Number(v) })} />
            <Field label="Días alerta advertencia" type="number" value={form.alert_days_warning} onChange={(v) => setForm({ ...form, alert_days_warning: Number(v) })} />
            <Field label="Umbral significancia AIA (/20)" type="number" value={form.significance_threshold} onChange={(v) => setForm({ ...form, significance_threshold: Number(v) })} />
            <Field label="Máximo de usuarios" type="number" value={form.max_users} onChange={(v) => setForm({ ...form, max_users: Number(v) })} />
          </div>
        </div>
      )}

      <div className="flex justify-end">
        <button className="btn btn-primary" onClick={save} disabled={saving} data-testid="settings-save">
          {saving ? <span className="spinner" /> : <><Save size={14} /> Guardar configuración</>}
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", helper, testId }) {
  return (
    <div>
      <label className="label">{label}</label>
      <input className="input" type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
      {helper && <div className="text-xs text-dim mt-2">{helper}</div>}
    </div>
  );
}
